import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import { useAmISuperadmin } from "@/api/invitations/useInvitations";
import { useAuth } from "@/auth/AuthContext";
import {
  isOrgMembershipRole,
  type OrgMembershipRole,
} from "@/auth/membershipRoles";

export type MembershipSnapshot = {
  organizationId: string;
  profileId: string;
  status: string;
  roles: readonly string[];
};

export type OrgPermissions = {
  isMember: boolean;
  roles: OrgMembershipRole[];
  isSuperadmin: boolean;
  /** Liste der Verwaltung: admin, Vorstand oder Superadmin. */
  canManageMembers: boolean;
  /**
   * Admin-Rolle vergeben oder entziehen.
   * Die Datenbank verlangt die Rolle admin in dieser Organisation.
   * Ein Superadmin ohne diese Rolle darf das nicht (set_member_roles).
   */
  canChangeAdminRole: boolean;
  /** Superadmin ohne eigene aktive Mitgliedschaft in dieser Organisation. */
  isReadOnlySuperadmin: boolean;
  /** set_member_roles / set_member_status: aktive Rolle admin oder Vorstand. */
  canWriteMemberChanges: boolean;
  /**
   * Admin-Mitglied deaktivieren oder reaktivieren:
   * Admin der Organisation, oder Superadmin mit admin/Vorstand in dieser Organisation.
   */
  canChangeAdminMemberStatus: boolean;
};

/**
 * Eigene Zeile eindeutig über profile_id und organization_id.
 * Mehr als eine Zeile ist ein Datenfehler und zählt nicht.
 */
export function ownMembership(
  memberships: readonly MembershipSnapshot[],
  userId: string,
  orgId: string,
): MembershipSnapshot | null {
  const matches = memberships.filter(
    (membership) =>
      membership.profileId === userId && membership.organizationId === orgId,
  );
  if (matches.length !== 1) return null;
  return matches[0];
}

export function resolveOrgPermissions(input: {
  userId: string | null;
  orgId: string | null;
  memberships: readonly MembershipSnapshot[];
  isSuperadmin: boolean;
}): OrgPermissions {
  const userId = input.userId;
  const orgId = input.orgId;
  const membership =
    userId && orgId ? ownMembership(input.memberships, userId, orgId) : null;
  const isMember = membership?.status === "active";
  const roles = isMember
    ? membership.roles.filter(isOrgMembershipRole)
    : [];
  const isAdmin = roles.includes("admin");
  const isBoard = roles.includes("board");
  const isSuperadmin = input.isSuperadmin;
  const canWriteMemberChanges = isMember && (isAdmin || isBoard);

  return {
    isMember,
    roles,
    isSuperadmin,
    canManageMembers: isSuperadmin || canWriteMemberChanges,
    canChangeAdminRole: isMember && isAdmin,
    isReadOnlySuperadmin: isSuperadmin && !isMember,
    canWriteMemberChanges,
    canChangeAdminMemberStatus:
      canWriteMemberChanges && (isAdmin || isSuperadmin),
  };
}

export type OrgAccessDecision = "loading" | "error" | "denied" | "allowed";

/**
 * Ladefehler schlägt „Kein Zugriff“. Sonst Mitglied oder Superadmin bei gültiger Id.
 */
export function orgAccessDecision(input: {
  validTenantId: boolean;
  loading: boolean;
  error: boolean;
  isMember: boolean;
  isSuperadmin: boolean;
}): OrgAccessDecision {
  if (input.loading) return "loading";
  if (input.error) return "error";
  if (input.validTenantId && (input.isMember || input.isSuperadmin)) return "allowed";
  return "denied";
}

export function useOrgPermissions(orgId: string | undefined) {
  const { user } = useAuth();
  const { memberships, loading, error: membershipError, refetch } = useMyOrganizations();
  const superadmin = useAmISuperadmin(Boolean(user?.id));
  const userId = user?.id ?? null;
  const permissions = resolveOrgPermissions({
    userId,
    orgId: orgId ?? null,
    memberships: memberships.map((membership) => ({
      organizationId: membership.organization_id,
      profileId: membership.profile_id,
      status: membership.status,
      roles: membership.roles,
    })),
    isSuperadmin: superadmin.data === true,
  });
  const own =
    userId && orgId
      ? memberships.filter(
          (membership) =>
            membership.profile_id === userId &&
            membership.organization_id === orgId,
        )
      : [];
  const orgName =
    own.length === 1 ? own[0]?.organization?.name ?? null : null;

  const loadError = membershipError ?? superadmin.error ?? null;

  return {
    ...permissions,
    orgName,
    loading: loading || superadmin.isLoading,
    loadError,
    refetch: () => {
      void refetch();
      void superadmin.refetch();
    },
  };
}
